import type { RefObject } from 'react';
import React, { useRef } from 'react';
import classNames from 'clsx';
import Link from '../../../../../../common/Link/Link';
import { isEmptyObject } from '../../../../../../utils/dataUtils';
import type { IMenuItemProps } from '../../../Menu.types';
import type { DropdownAnimationNameType } from '../../../constants';
import { createSDKAction } from './utils';
import classes from './style/MenuItem.module.scss';
import { MenuParts, selectors, DesignStates } from '../../../constants';
import { isCurrentItem } from '../../../menuItems';
import { AnimationPhase, type AnimationState } from './useAnimationState';
import { MenuItemDropdownIcon } from './MenuItemDropdownIcon';
import { useIconAnimation } from './useIconAnimation';

export const MenuItemLabel: React.FC<
  IMenuItemProps & {
    hasDropdownMenu: boolean;
    hasSubItems: boolean;
    hasContainer: boolean;
    chevronButtonRef: RefObject<HTMLButtonElement>;
    toggleDropdown: () => void;
    isOpen: boolean;
    dropdownAnimationState: AnimationState<DropdownAnimationNameType>;
    submenuToggleId?: string;
    itemClassName?: string;
  }
> = props => {
  const {
    item,
    currentItem,
    hasDropdownMenu,
    hasSubItems,
    hasContainer,
    onItemClick,
    onItemDblClick,
    onItemMouseIn,
    onItemMouseOut,
    partToPreviewStateMap,
    translations,
    chevronButtonRef,
    toggleDropdown,
    isOpen,
    dropdownAnimationState,
    submenuToggleId,
    itemClassName,
    BuilderAnimationIcon,
  } = props;

  const isCurrentPage = isCurrentItem(item, currentItem);
  const { label, link } = item;
  const hasLink = !isEmptyObject(link);
  const linkRef: RefObject<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null);
  const srOnlyChevronButtonRef = useRef<HTMLButtonElement>(null);
  const itemLabelClassName =
    props.menuElementProps?.item?.elementProps?.itemLabel?.className;

  const labelContainerClassNames = classNames(
    selectors.item,
    classes.labelContainer,
    itemClassName,
    isCurrentPage && DesignStates.item.selected.className,
  );

  const isInteractive = (hasSubItems || hasLink) && !isCurrentPage;

  const isDropdownIconOpen =
    isOpen && dropdownAnimationState.phase !== AnimationPhase.ExitActive;
  const mouseEnterAction = createSDKAction(item, isCurrentPage, onItemMouseIn);
  const mouseLeaveAction = createSDKAction(item, isCurrentPage, onItemMouseOut);
  const { iconAnimatedRef, handleMouseEnter, handleMouseLeave } =
    useIconAnimation({
      BuilderAnimationIcon,
      isCurrentPage,
      isOpen,
      onMouseEnter: mouseEnterAction,
      onMouseLeave: mouseLeaveAction,
    });

  return (
    <div
      className={labelContainerClassNames}
      data-selected={isCurrentPage ? true : undefined}
      data-part={MenuParts.MenuItemContent}
      data-preview={partToPreviewStateMap?.item}
      data-interactive={isInteractive}
      onClick={createSDKAction(item, isCurrentPage, onItemClick)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={createSDKAction(item, isCurrentPage, onItemDblClick)}
    >
      <Link
        ref={linkRef}
        {...link}
        dataPart={MenuParts.MenuItemLink}
        activateByKey="Enter"
        {...(isCurrentPage && {
          'aria-current': 'page',
        })}
      >
        <span className={classes.labelWrapper}>
          {BuilderAnimationIcon && (
            <BuilderAnimationIcon
              ref={iconAnimatedRef}
              data-testid="menu-builder-animation-icon"
            />
          )}
          <span
            className={classNames(
              selectors.itemLabel,
              classes.label,
              itemLabelClassName,
            )}
            data-part={MenuParts.Label}
          >
            {label}
          </span>
        </span>
      </Link>
      {hasDropdownMenu &&
        ['default', 'srOnly'].map(variant => (
          <MenuItemDropdownIcon
            key={variant}
            ariaLabel={translations.dropdownButtonAriaLabel?.replace(
              '<%= itemName %>',
              label,
            )}
            chevronButtonRef={
              variant === 'srOnly' ? srOnlyChevronButtonRef : chevronButtonRef
            }
            onClick={toggleDropdown}
            isOpen={isOpen}
            hasSubItems={hasSubItems}
            hasContainer={hasContainer}
            hasLink={hasLink}
            isDropdownIconOpen={isDropdownIconOpen}
            className={variant === 'srOnly' ? classes.srOnly : undefined}
            srOnly={variant === 'srOnly'}
            submenuToggleId={variant === 'srOnly' ? undefined : submenuToggleId}
          />
        ))}
    </div>
  );
};
